import { useState, useMemo } from 'react';
import { useCamera } from '@pella/barcode-scanner';
import { ImQrcode as ScanIcon } from 'react-icons/im';

import { useOrgItems } from '../../../hooks/useInvManagement';
import { useDebounce } from '../../../hooks';
import styles from './FullInvPartSelector.module.css';

/**
 * FullInvPartSelector component for selecting inventory parts
 * @param {object} props - Component props
 * @param {(part: object) => void} props.partOnChange - Callback when part is selected
 * @param {string} props.selectedOrg - Selected organization ID
 * @param {boolean} props.showTitle - Whether to show the title
 * @param {string} props.title - Title text to display
 * @returns {import("react").ReactElement}
 */
const FullInvPartSelector = (props) => {
  const { startCamera } = useCamera();

  const selectedOrg = props?.selectedOrg;
  const [selectedPart, setSelectedPart] = useState(null);
  const [selectedItem, setSelectedItem] = useState('');
  const [selectedDescription, setSelectedDescription] = useState('');

  const debouncedItem = useDebounce(selectedItem, 2000);
  const debouncedDescription = useDebounce(selectedDescription, 2000);

  const { orgItems } = useOrgItems({ orgId: selectedOrg, item: debouncedItem, descr: debouncedDescription });

  const sortedPartsList = useMemo(() => {
    if (!orgItems?.length) return [];
    return [...orgItems].toSorted((a, b) => {
      if (a.item < b.item) return -1;
      if (a.item > b.item) return 1;
      return 0;
    });
  }, [orgItems]);

  // If selectedPart is no longer in the current results (org/filters changed), treat it as unset
  const resolvedPart = useMemo(
    () => sortedPartsList.find((p) => p.item === selectedPart?.item) ?? null,
    [selectedPart, sortedPartsList],
  );

  const partOnChange = (e) => {
    setSelectedPart(e);
    props.partOnChange(e);
  };

  const handleScannerRead = (value) => {
    setSelectedItem(value);
  };

  const handleScannerOpen = () => {
    startCamera(handleScannerRead);
  };

  return (
    <div className={styles.container}>
      {props.showTitle ? (
        <div className={styles.header}>
          <div className={styles.headerContent}>
            <h3>{props.title || 'Select Item'}</h3>
            <span className={styles.badge}>{orgItems?.length ?? 0}</span>
          </div>
        </div>
      ) : null}

      <div className={styles.formRow}>
        <div className={styles.formColumn}>
          {/* eslint-disable-next-line jsx-a11y/label-has-associated-control */}
          <label className={styles.formLabel}>Item</label>
          <div className={styles.inputWithButton}>
            <input
              id='full-inv-item-input'
              className={styles.formInput}
              type='text'
              value={selectedItem}
              onChange={(e) => {
                setSelectedItem(e.target.value);
                setSelectedPart(null);
              }}
              placeholder='Enter item number'
            />
            <button
              type='button'
              className={styles.iconButton}
              onClick={handleScannerOpen}
              title='Scan QR code'
              aria-label='scanner'
            >
              <ScanIcon />
            </button>
          </div>
        </div>

        <div className={styles.formColumn}>
          {/* eslint-disable-next-line jsx-a11y/label-has-associated-control */}
          <label className={styles.formLabel}>Description</label>
          <input
            id='full-inv-desc-input'
            className={styles.formInput}
            type='text'
            value={selectedDescription}
            onChange={(e) => {
              setSelectedDescription(e.target.value);
              setSelectedPart(null);
            }}
            placeholder='Enter description'
          />
        </div>
      </div>

      <div className={styles.formGroup}>
        {/* eslint-disable-next-line jsx-a11y/label-has-associated-control */}
        <label className={styles.formLabel}>Select Part*</label>
        <select
          id='full-inv-part-select'
          className={styles.selectInput}
          value={resolvedPart ? JSON.stringify(resolvedPart) : ''}
          onChange={(e) => {
            if (e.target.value) {
              const part = sortedPartsList.find((p) => JSON.stringify(p) === e.target.value);
              partOnChange(part);
            }
          }}
        >
          <option value=''>Choose a part...</option>
          {sortedPartsList.map((part) => (
            <option key={part?.item ?? JSON.stringify(part)} value={JSON.stringify(part)}>
              ({part?.item}) {part?.description}
            </option>
          ))}
        </select>
      </div>
    </div>
  );
};

export default FullInvPartSelector;
